import {Alert, Box, Button, Divider, Fade, IconButton, Modal, Paper, Stack, styled, Tooltip, Typography} from "@mui/material";
import {
    CameraAltRounded,
    CancelRounded,
    DoneRounded,
    MicRounded,
    RecordVoiceOver,
    UploadFileRounded
} from "@mui/icons-material";
import {useEffect, useRef, useState} from "react";
import LoadingSpinner from "../../loading/loadingSpinner";
import {useParams} from "react-router-dom";
import {uploadFile} from "../../../lib/services/drive";
import ProgressLine from "../../../theme/components/ProgressLine";
import {red} from "@mui/material/colors";
import {convertSize} from "../../../lib/commonFunctions/convertSize";
import UploadInput from "./uploadInput";
import {compressString} from "../../../lib/commonFunctions/compressString";

const UploadFileModal = (props) => {

    const {onClose, setHasUploaded, ...restProps} = props
    const {folderId} = useParams();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploadDone, setUploadDone] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [isUploading, setIsUploading] = useState(false);

    const isAndroid = navigator?.userAgent ? /(android)/i.test(navigator.userAgent) : false;
    const recAudioRef = useRef(null);
    const recImageRef = useRef(null);

    const handleSetProgress = (payload, file, progress) => {
        const newSelectedFiles = [...payload];
        const fileIndex = newSelectedFiles.indexOf(file);
        if (fileIndex < 0) return;
        newSelectedFiles[fileIndex].progress = progress;
        setSelectedFiles(newSelectedFiles);
    }
    const handleAbort = (file) => {
        if (file.isCompleted) return;
        const newFiles = [...selectedFiles];
        const index = newFiles.indexOf(file);
        if (index < 0) return;
        const selectedFile = newFiles[index];
        selectedFile.controller.abort();
        selectedFile.isAborted = true;
        setSelectedFiles(newFiles);
    }
    const handleAbortAll = async (options) => {
        const {cleanup, closeAfter} = options;
        const newFiles = [...selectedFiles];
        for await (const file of newFiles) {
            if (!file.isCompleted) {
                await file.controller.abort();
                file.isAborted = true;
            }
        }
        if (!cleanup) setSelectedFiles(newFiles);
        else setSelectedFiles([]);
        if (closeAfter) handleOnClose();
    }
    const handleOnUpload = async (files) => {
        if (!files?.length) return;
        setUploadDone(false);
        setIsUploading(true);
        const uploadingFiles = [...files];
        uploadingFiles.forEach(file => {
            file.controller = new AbortController();
            file.isCompleted = false;
            file.progress = 0;
        });
        setSelectedFiles(uploadingFiles);
        // This "for" method will upload files one by one
        // if you want multiple uploads at the same time use forEach method.
        for await (const file of uploadingFiles) {
            const response = await uploadFile(file, (p) => handleSetProgress(uploadingFiles, file, p), folderId);
            const data = response?.data;
            const fileIndex = uploadingFiles.indexOf(file);
            if (data && data.isSuccess) {
                if (fileIndex >= 0) {
                    uploadingFiles[fileIndex].isCompleted = true;
                    setSelectedFiles(uploadingFiles);
                }
                setUploadedFiles([...uploadedFiles, file]);
            }
            if (data && data.errors?.length) {
                if (fileIndex >= 0) {
                    uploadingFiles[fileIndex].isAborted = true;
                    uploadingFiles[fileIndex].errors = data.errors;
                    uploadingFiles[fileIndex].isCompleted = false;
                }
            }
        }
        setSelectedFiles(uploadingFiles);
        setIsUploading(false);
    }
    const handleOnClose = () => {
        if (uploadedFiles.length) setHasUploaded(true);
        setUploadedFiles([]);
        onClose();
    }


    useEffect(() => {
        if (selectedFiles?.length) {
            const unCompletedFiles = selectedFiles.filter(f => f.isCompleted === false);
            // All Files Uploaded
            if (!unCompletedFiles?.length) {
                setHasUploaded(true);
                setUploadDone(true);
            }
        }
    }, [selectedFiles])

    return <StyledModal onClose={handleOnClose} {...restProps}>
        <Paper elevation={25} sx={{ width: {xs: "90%", lg: "auto" }, minWidth: { lg: "25%" }, maxWidth: {xs: "90%", lg: "85%", xl: "75%", xxl: "65%"}, maxHeight: "90%", minHeight: "50%", p: 2.5, borderRadius: 2.5, overflowY: "auto" }}>
            <Typography textAlign="center" fontWeight={500}>آپلود فایل جدید</Typography>
            <UploadInput disabled={isUploading} onChange={handleOnUpload} sx={{ mt: 3 }}/>
            {isAndroid && (
                <Stack mt={2} spacing={1.5}>
                    <Button onClick={() => recAudioRef.current?.click()} variant="outlined" fullWidth startIcon={<MicRounded/>} sx={{ py: 1.5, borderRadius: 2 }}>ضبط صدا</Button>
                    <input type="file" capture="user" accept="audio/*" ref={recAudioRef} onChange={(e) => handleOnUpload(e.target.files)} style={{ display: "none" }}/>
                    <Button onClick={() => recImageRef.current?.click()} variant="outlined" fullWidth startIcon={<CameraAltRounded/>} sx={{ py: 1.5, borderRadius: 2 }}>ضبط تصویر</Button>
                    <input type="file" capture="user" accept="image/*" ref={recImageRef} onChange={(e) => handleOnUpload(e.target.files)} style={{ display: "none" }}/>
                </Stack>
            )}
            {/* Uploading Files */}
            {selectedFiles.length
                ?
                <>
                    <Stack mt={5} spacing={2}>
                        {selectedFiles.map((f, ind) => (
                            <UploadItem key={ind} file={f} onAbort={handleAbort}/>
                        ))}
                    </Stack>
                    <Stack mt={5} spacing={2}>
                        {!uploadDone && (
                            <Button
                                onClick={() => handleAbortAll({cleanup: true, closeAfter: true})} fullWidth
                                variant="contained" color="error"
                                sx={{ py: 1.5}} disableElevation>انصراف</Button>
                        )}
                    </Stack>
                </>
                : null}
            <Button
                onClick={() => {handleOnClose();setSelectedFiles([])}} fullWidth
                variant="contained"
                sx={{ py: 1.5, mt: 2}} disableElevation>بستن</Button>
        </Paper>
    </StyledModal>
}
export default UploadFileModal;

const UploadItem = (props) => {

    const {file, onAbort, ...restProps} = props;
    const {name, size, progress, isAborted, isCompleted, errors} = file;
    const fileSize = convertSize(size);

    return (
        <Stack spacing={1.5} {...restProps}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
                <Typography color="inherit">{`${compressString(name, 24)} ${isAborted ? " - لغو شده" : ` - ${fileSize}` }`}</Typography>
                <Stack direction="row" alignItems="center" spacing={1.5}>
                    {!isCompleted && !isAborted && (
                        <Tooltip title="لغو آپلود">
                            <IconButton onClick={() => onAbort(file)} color="error"><CancelRounded/></IconButton>
                        </Tooltip>
                    )}
                </Stack>
            </Box>
            <Box display="flex" alignItems="center">
                <ProgressLine
                    percent={progress} isCompleted={isCompleted}
                    sx={{ mr: 2, "&:before": {background: isAborted && red[500]} }}/>
                {!isAborted && (
                    !isCompleted
                        ? <LoadingSpinner/>
                        : <DoneRounded color="success"/>
                )}
            </Box>
            {errors?.length && (
                <Fade in={!!errors.length}>
                    <Stack spacing={1.5}>
                        {errors.map((err, ind) => (
                            <Alert key={ind} severity="error">{err}</Alert>
                        ))}
                    </Stack>
                </Fade>
            )}
            <Divider/>
        </Stack>
    )
}

const StyledModal = styled(Modal)(({theme}) => ({
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
}));